import useGetTableName from '@features/user/shared/hooks/useGetTableName';
import {
  ActionIcon,
  Box,
  Button,
  Col,
  Container,
  Flex,
  Grid,
  Skeleton,
  Stack,
  TextInput,
  createStyles,
} from '@mantine/core';
import { useDebouncedValue, useElementSize } from '@mantine/hooks';
import cartSlice from '@reducers/cart';
import { useVendorAvailability } from '@services/availabilitytime/hooks/useVendorAvailability';
import { IconSearch } from '@tabler/icons-react';
import arrayUtil from '@utils/arrayUtil';
import storageUtil from '@utils/storageUtil';
import { safeString } from '@utils/stringUtil';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-scroll';
import starkString from 'starkstring';
import { AppDispatch } from '../../../../stores';
import { HomePageProps } from '../types';
import MenuCategory from './menuCategory';

const useStyles = createStyles((theme) => ({
  button: {
    border: 0,
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    cursor: 'pointer',
    fontSize: theme.fontSizes.xs,
    fontWeight: 700,

    '&.activeMenu': {
      backgroundColor: theme.colors.brand[7],
      color: theme.white,

      '&:hover': {
        backgroundColor: theme.colors.brand[9],
      },
    },

    '&:hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
}));

interface VendorMenuProps {
  vendor: HomePageProps['vendor'];
  isMobile: boolean;
}

const VendorMenu: React.FC<VendorMenuProps> = ({ vendor, isMobile }) => {
  const router = useRouter();
  const dispatch: AppDispatch = useDispatch();
  const { ref, height } = useElementSize();
  const { table } = router.query;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 400);
  const { tableName, loading } = useGetTableName();

  useVendorAvailability();

  useEffect(() => {
    if (table) {
      dispatch(cartSlice.actions.setTableNumber(arrayUtil.arrayOrItemToItem(table)));
    } else {
      dispatch(cartSlice.actions.setTableNumber(undefined));
    }
  }, [dispatch, vendor?.id, table]);

  const scrollMenuToSelectedCategory = useCallback((id: string) => {
    const target = document.getElementById(`cat-${id}`);
    const container = document.getElementById('scroll-menu-container');

    if (target && container) {
      const rect = target.getBoundingClientRect();
      const left = rect.left + window.scrollX;

      container.scrollLeft += left - 30;
    }
  }, []);

  const { classes } = useStyles();

  const scrollMenuItems = vendor?.menuCategories
    .filter((category) => category.menuItems.length && !category.menuItems.every((m) => !m.active))
    .map((category) => (
      <Link
        key={`category-${category.title}`}
        id={`cat-${category.title}`}
        to={safeString(category?.title)}
        offset={-(height + 10)}
        onSetActive={scrollMenuToSelectedCategory}
        activeClass="activeMenu"
        className={classes.button}
        type="button"
        spy
      >
        {category.title}
      </Link>
    ));

  const mappedCategories = vendor?.menuCategories?.map((menuCategory) => {
    const { menuItems } = menuCategory;
    const newMenuItems = menuItems?.map((menuItem) => {
      const imageUrl = storageUtil.getItemImage(
        safeString(vendor.subDomain),
        menuItem?.images && menuItem?.images.length > 0 ? menuItem?.images[0] : ''
      );

      return {
        ...menuItem,
        withTax: menuItem.withTax,
        imageUrl,
      };
    });

    return {
      ...menuCategory,
      menuItems: newMenuItems,
    };
  });

  const scrollMenu = (
    <Flex
      gap="xs"
      direction="row"
      wrap="nowrap"
      id="scroll-menu-container"
      sx={() => ({
        whiteSpace: 'nowrap',
        overflowX: 'auto',
        '::-webkit-scrollbar': {
          display: 'none',
        },
      })}
    >
      {scrollMenuItems}
    </Flex>
  );

  const searchInput = (
    <Grid gutter="xs">
      {table?.toString() ? (
        <Col span="content">
          {loading ? (
            <Skeleton w={80} h={40} />
          ) : (
            <Button variant="light" sx={{ backgroundColor: '#f1f3f5' }}>
              میز: {tableName}
            </Button>
          )}
        </Col>
      ) : null}
      <Col span="auto">
        <TextInput
          variant="filled"
          placeholder="جستجوی غذا"
          value={searchTerm}
          rightSection={
            <ActionIcon variant="transparent" color="gray.3">
              <IconSearch />
            </ActionIcon>
          }
          onChange={(event) => setSearchTerm(starkString(event.currentTarget.value).persianChar().toString())}
        />
      </Col>
    </Grid>
  );

  const categories = mappedCategories?.map((menuCategory) => (
    <MenuCategory
      key={`cat-${menuCategory.title}`}
      search={debouncedSearchTerm}
      menuCategory={menuCategory}
      isMobile={isMobile}
    />
  ));

  return (
    <>
      <Box
        ref={ref}
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: 50,
          backgroundColor: theme.white,
        })}
        py="xs"
      >
        <Container size="lg">
          <Stack spacing="xs">
            {scrollMenu}
            {searchInput}
          </Stack>
        </Container>
      </Box>

      <Container size="lg">
        <Stack>
          {categories}
          <Box my={30} />
        </Stack>
      </Container>
    </>
  );
};

export default VendorMenu;
